/**
 * Snippets Store - Centralized State Management
 *
 * This module provides a centralized store for managing snippets data
 * using react-sweet-state. This replaces the previous pattern of individual hooks
 * (useCreateSnippet, useFetchSnippets) with a single source of truth.
 *
 * Usage:
 * - Wrap your app with <SnippetsProvider> to trigger initial fetch
 * - Use useSnippetsState() for read-only state access
 * - Use useSnippetsActions() when you need to dispatch actions
 * - Use useSnippets() when you need both state and actions
 */
import {
	createStore,
	createHook,
	createActionsHook,
	createStateHook,
	defaultRegistry,
} from 'react-sweet-state';

import { onCurrentFetchComplete as onCurrentFetchCompleteUtil } from '../utils/pendingCallbacks.ts';

import type { SnippetsState } from './snippetsStore.types.ts';
import { getInitialState } from './snippetsStore.types.ts';
import { actions } from './actions/index.ts';

export const SnippetsStore = createStore({
	initialState: getInitialState(),
	actions,
	name: 'SnippetsStore',
});

/** Hook to access both state and actions - use when you need full store access */
export const useSnippets = createHook(SnippetsStore);

/** Hook to access only actions - use when component only dispatches actions */
export const useSnippetsActions = createActionsHook(SnippetsStore);

/** Hook to access only state - use for read-only state consumption */
export const useSnippetsState = createStateHook(SnippetsStore);

/**
 * Gets the current snippets state without subscribing to updates.
 * Use this when you need a one-time snapshot of the state outside of React's render cycle,
 * or when you want to avoid re-renders when the state changes.
 */
export const getSnippetsStateSnapshot = (): SnippetsState => {
	const storeInstance = defaultRegistry.getStore(SnippetsStore);
	return storeInstance.storeState.getState();
};

export const onCurrentFetchComplete = onCurrentFetchCompleteUtil;
